// Library methods
import { useCallback, useMemo } from "react";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import WebRtc from "src/components/containers/shared/LiveUpdateModal/LiveUpdateContent/WebRtc/WebRtc";
import DryEyeSimulationOptions from "./DryEyeSimulationOptions/DryEyeSimulationOptions";
import DryEyeSelectionCard from "./DryEyeSelectionCard/DryEyeSelectionCard";
import { LensParameterUpdate } from "../LensSimulation/LensSimulation";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Services
import { deviceUpdateLensParameter } from "src/services/signalR/api";

// Constants
import { DEFAULT_SIM_ENVIRONMENT } from "src/constants/enums";

// Utils
import { isLensViewed } from "src/utils/exams/lensSimulationHelper";

type DryEyeSimulationType = {
  examData: ExamSectionReportType | undefined;
  connection: HubConnection | null;
};

const DryEyeSimulation = ({ examData, connection }: DryEyeSimulationType) => {
  const { device } = useLiveUpdateContext();
  const { getToken } = useTokenAccess();

  const currentSimEnv = useMemo(
    () => examData?.state?.simulationEnvironment ?? DEFAULT_SIM_ENVIRONMENT,
    [examData?.state?.simulationEnvironment]
  );

  // Dry eye simulations report their profiles under state.profiles, not
  // state.lenses
  const profiles = useMemo(
    () => examData?.state?.profiles ?? [],
    [examData?.state?.profiles]
  );

  const currentProfile = useMemo(
    () => profiles.find((profile) => isLensViewed(profile)) ?? profiles[0],
    [profiles]
  );

  const isDeviceReady = Boolean(device?.id && connection);
  const isCurrentProfileActionDisabled =
    !isDeviceReady || !currentProfile?.name;

  // Every profile update goes through here, named by profile
  const sendProfileUpdate = useCallback(
    async (profileName: string, update: LensParameterUpdate) => {
      if (!device?.id || !connection) return;

      try {
        const token = await getToken();
        await deviceUpdateLensParameter(
          token,
          device.id,
          { lensName: profileName, ...update },
          connection
        );
      } catch (e) {
        console.error(e);
      }
    },
    [device?.id, connection, getToken]
  );

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr" },
          gap: "8px",
        }}
      >
        <Box sx={{ minHeight: "300px", maxHeight: "350px" }}>
          <WebRtc connection={connection} />
        </Box>
        {profiles.length === 0 ? (
          <Typography sx={{ fontSize: "14px" }}>N/A</Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
              },
              gap: "8px",
            }}
          >
            {profiles.map((profile) => (
              <DryEyeSelectionCard
                key={profile.name}
                profile={profile}
                isDeviceReady={isDeviceReady}
                connection={connection}
              />
            ))}
          </Box>
        )}
        <DryEyeSimulationOptions
          connection={connection}
          currentProfile={currentProfile}
          currentSimEnv={currentSimEnv}
          isCurrentProfileActionDisabled={isCurrentProfileActionDisabled}
          sendProfileUpdate={sendProfileUpdate}
        />
      </Box>
    </Box>
  );
};

export default DryEyeSimulation;
